/**
 * Host Nonsole Participation — shared contract
 *
 * Classifies each reachable machine into exactly one participation bucket using
 * a fixed severity precedence. Consumed by realtime-api aggregation, daily
 * snapshots, and the admin-dashboard Host Nonsole Participation page.
 */

import { z } from 'zod';

// ============================================================================
// Buckets
// ============================================================================

// Precedence order (also drives display order). Real readiness gates rank
// ABOVE owner/attestation: owner attribution no longer gates nonsole entry (see
// node-service `updateUnattendedConfiguration`), so a host blocked for a real
// reason (version, drivers, power, idle, ...) must be bucketed by that reason
// rather than misreported as "needs sign-in". NO_OWNER / NOT_ATTESTED are now
// informational (attribution only) and only surface once nothing real blocks
// the host.
export const participationBucketValues = [
  'IN_HOST_MODE_HOSTING',
  'IN_HOST_MODE_IDLE',
  'POWERED_OFF',
  'SLEEPING',
  'POWER_UNKNOWN',
  'BELOW_MIN_VERSION',
  'DRIVERS_BROKEN',
  'AC_POWER',
  'IDLE_TIMER',
  'ROLLOUT_ENABLED',
  'SESSION_COUNT',
  'PRESENCE_HOLD',
  'OTHER_BLOCKED',
  'NO_OWNER',
  'NOT_ATTESTED',
  'NO_READINESS_DATA',
  'READY_NOT_HOSTING',
] as const;

export type ParticipationBucket = (typeof participationBucketValues)[number];

export const participationBucketSchema = z.enum(participationBucketValues);

/**
 * Gate IDs that classify as "drivers broken" (override AC_POWER / IDLE_TIMER).
 * Only true driver-health gates (category 'health') belong here. Environment
 * gates like VIRTUAL_CONTROLLER (XInput slot availability) are NOT driver
 * health and fall through to OTHER_BLOCKED.
 */
export const DRIVER_GATE_IDS = ['INPUT_DRIVERS_HEALTHY', 'PLAYCAST_DRIVERS_HEALTHY'] as const;

export const PARTICIPATION_BUCKET_LABELS: Record<ParticipationBucket, string> = {
  IN_HOST_MODE_HOSTING: 'In Host Mode (hosting)',
  IN_HOST_MODE_IDLE: 'In Host Mode (idle)',
  POWERED_OFF: 'Powered off',
  SLEEPING: 'Sleeping',
  POWER_UNKNOWN: 'Unknown power status',
  NO_OWNER: 'No owner',
  NOT_ATTESTED: 'Owner claimed — no attestation',
  BELOW_MIN_VERSION: 'Below minimum version',
  DRIVERS_BROKEN: 'Drivers broken',
  AC_POWER: 'Not on AC power',
  IDLE_TIMER: 'User not idle long enough',
  ROLLOUT_ENABLED: 'Nonsole rollout not enabled',
  SESSION_COUNT: 'No spare Windows session slot',
  PRESENCE_HOLD: 'App/driver keeping host awake',
  OTHER_BLOCKED: 'Blocked (other gate)',
  NO_READINESS_DATA: 'No readiness data',
  READY_NOT_HOSTING: 'Ready — not in Host Mode',
};

/**
 * Display order: success buckets first, then non-participation by precedence.
 * `participationBucketValues` is already declared in that order, so reuse it as
 * the single source of truth rather than duplicating the list.
 */
export const PARTICIPATION_BUCKET_DISPLAY_ORDER: ParticipationBucket[] = [
  ...participationBucketValues,
];

/** Non-participation buckets only (excludes in-host-mode success buckets). */
export const NON_PARTICIPATION_BUCKETS: ParticipationBucket[] =
  PARTICIPATION_BUCKET_DISPLAY_ORDER.filter(
    (b) => b !== 'IN_HOST_MODE_HOSTING' && b !== 'IN_HOST_MODE_IDLE',
  );

export const DEFAULT_REACHABILITY_DAYS = 7;
export const MAX_REACHABILITY_DAYS = 90;

/** Node-service heartbeat interval — mirrors apps/node-service/src/realtime/index.ts. */
export const REACHABILITY_HEARTBEAT_MS = 5 * 60 * 1000;
/** Grace period on top of the heartbeat interval to account for network jitter. */
export const REACHABILITY_GRACE_MS = 60 * 1000;
/** Total window: a machine is "connected now" when nodeServiceLastSeenAt is within this. */
export const REACHABILITY_WINDOW_MS = REACHABILITY_HEARTBEAT_MS + REACHABILITY_GRACE_MS;

/**
 * Windows `Machine.lastKnownState` values that indicate the machine is
 * ACTIVELY in Host Mode (Playcast session running or ready to accept one).
 *
 * Shared source of truth: both HNP drill-downs and Fleet's `hostMode` filter
 * key off this list, so a machine's Host-Mode classification is identical
 * across every surface. Guests-only: coupled with `guestAccount: true` at
 * every callsite because host mode without a guest account is not
 * considered "in host mode" for participation purposes.
 */
export const HOST_PRESENT_STATES: readonly string[] = ['online', 'idle', 'hosting', 'busy'];

// ============================================================================
// Classifier
// ============================================================================

/** Query-time power display state (matches FleetService.derivePowerDisplay output). */
export type PowerDisplayState = 'awake' | 'asleep' | 'off' | 'unknown';

export interface ParticipationMachineSignals {
  isCloudHost: boolean;
  hasOwner: boolean;
  isAttested: boolean;
  belowMinVersion: boolean;
  blockingGateIds: string[];
  readinessFresh: boolean;
  inHostMode: boolean;
  isHosting: boolean;
  /** Derived from machine power fields + staleness. Omitted/awake → fall through to readiness gates. */
  powerDisplayState?: PowerDisplayState;
}

export function classifyParticipation(signals: ParticipationMachineSignals): ParticipationBucket {
  if (signals.inHostMode) {
    return signals.isHosting ? 'IN_HOST_MODE_HOSTING' : 'IN_HOST_MODE_IDLE';
  }

  // Power state dominates readiness gates: a machine that is off, asleep, or has
  // lost contact (scoped unknown from stale awake / stored unknown) is diagnosed
  // by power before version/drivers/idle gates.
  if (signals.powerDisplayState === 'off') {
    return 'POWERED_OFF';
  }
  if (signals.powerDisplayState === 'asleep') {
    return 'SLEEPING';
  }
  if (signals.powerDisplayState === 'unknown') {
    return 'POWER_UNKNOWN';
  }

  // Real readiness gates first. Owner attribution no longer blocks nonsole
  // entry, so a host held back by version / drivers / power / idle / other
  // gates is diagnosed by that gate rather than by its (now non-blocking)
  // owner or attestation state.
  if (signals.belowMinVersion) {
    return 'BELOW_MIN_VERSION';
  }
  if (DRIVER_GATE_IDS.some((id) => signals.blockingGateIds.includes(id))) {
    return 'DRIVERS_BROKEN';
  }
  if (signals.blockingGateIds.includes('AC_POWER')) {
    return 'AC_POWER';
  }
  if (signals.blockingGateIds.includes('IDLE_TIMER')) {
    return 'IDLE_TIMER';
  }
  // The three highest-volume gates that were previously lumped into
  // OTHER_BLOCKED, broken out into their own buckets. Ordered among themselves by
  // the host's registry (gate-evaluation) order: ROLLOUT_ENABLED (prerequisite)
  // then SESSION_COUNT (prerequisite) then PRESENCE_HOLD (entry condition). They
  // sit below AC_POWER/IDLE_TIMER so those existing buckets are unchanged — only
  // the OTHER_BLOCKED catch-all is subdivided.
  //   ROLLOUT_ENABLED : nonsole rollout is not enabled for this machine.
  //   SESSION_COUNT   : no spare Windows session slot (someone is signed in).
  //   PRESENCE_HOLD   : an app/driver power-request hold or live fullscreen
  //                     window is keeping the host awake (distinct from
  //                     IDLE_TIMER's user-input recency — the human may be away).
  if (signals.blockingGateIds.includes('ROLLOUT_ENABLED')) {
    return 'ROLLOUT_ENABLED';
  }
  if (signals.blockingGateIds.includes('SESSION_COUNT')) {
    return 'SESSION_COUNT';
  }
  if (signals.blockingGateIds.includes('PRESENCE_HOLD')) {
    return 'PRESENCE_HOLD';
  }
  if (signals.blockingGateIds.length > 0) {
    return 'OTHER_BLOCKED';
  }

  // Informational (attribution only): surfaced once nothing real blocks the
  // host. These do NOT prevent the host from entering nonsole mode.
  if (!signals.isCloudHost && !signals.hasOwner) {
    return 'NO_OWNER';
  }
  if (!signals.isCloudHost && signals.hasOwner && !signals.isAttested) {
    return 'NOT_ATTESTED';
  }

  if (!signals.readinessFresh) {
    return 'NO_READINESS_DATA';
  }
  return 'READY_NOT_HOSTING';
}

export function isReadinessFresh(
  evaluatedAtUtc: string | null | undefined,
  staleMinutes: number,
  nowMs: number = Date.now(),
): boolean {
  if (!evaluatedAtUtc || evaluatedAtUtc.trim().length === 0) {
    return false;
  }
  const evaluatedMs = Date.parse(evaluatedAtUtc);
  if (!Number.isFinite(evaluatedMs)) {
    return false;
  }
  // A timestamp ahead of now (clock skew or bogus data) is not trustworthy —
  // treat it as not fresh rather than letting the negative age pass the window.
  if (evaluatedMs > nowMs) {
    return false;
  }
  return nowMs - evaluatedMs <= staleMinutes * 60 * 1000;
}

export function clampReachabilityDays(input: number | undefined): number {
  if (typeof input !== 'number' || !Number.isFinite(input)) {
    return DEFAULT_REACHABILITY_DAYS;
  }
  if (input < 1) return 1;
  if (input > MAX_REACHABILITY_DAYS) return MAX_REACHABILITY_DAYS;
  return Math.floor(input);
}

export function parseReachabilityDaysParam(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (typeof raw !== 'string') return undefined;
  const parsed = Number(raw);
  if (
    !Number.isFinite(parsed) ||
    !Number.isInteger(parsed) ||
    parsed < 1 ||
    parsed > MAX_REACHABILITY_DAYS
  ) {
    throw new Error(`reachabilityDays must be a positive integer <= ${MAX_REACHABILITY_DAYS}`);
  }
  return parsed;
}

// ============================================================================
// API response shapes
// ============================================================================

export const participationReasonRowSchema = z.object({
  bucket: participationBucketSchema,
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type ParticipationReasonRow = z.infer<typeof participationReasonRowSchema>;

export const hostNonsoleParticipationTotalsSchema = z.object({
  allActive: z.number().int().nonnegative(),
  connectedNow: z.number().int().nonnegative(),
  disconnected: z.number().int().nonnegative(),
  uninstalled: z.number().int().nonnegative(),
  /** @deprecated Legacy alias for connectedNow — kept for one release so old snapshots parse. */
  reachable: z.number().int().nonnegative().optional(),
  /** @deprecated Legacy alias for disconnected — kept for one release so old snapshots parse. */
  dormant: z.number().int().nonnegative().optional(),
});
export type HostNonsoleParticipationTotals = z.infer<typeof hostNonsoleParticipationTotalsSchema>;

export const hostNonsoleParticipationInHostModeSchema = z.object({
  hosting: z.number().int().nonnegative(),
  idle: z.number().int().nonnegative(),
});
export type HostNonsoleParticipationInHostMode = z.infer<
  typeof hostNonsoleParticipationInHostModeSchema
>;

export const hostNonsoleParticipationSummarySchema = z.object({
  generatedAt: z.number(),
  /** @deprecated No-op since the reachable window is now fixed at 5 min + 1 min grace. Echoed for backward compat; will be removed in a future release. */
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  totals: hostNonsoleParticipationTotalsSchema,
  inHostMode: hostNonsoleParticipationInHostModeSchema,
  reasons: z.array(participationReasonRowSchema),
});
export type HostNonsoleParticipationSummary = z.infer<typeof hostNonsoleParticipationSummarySchema>;

/**
 * Snapshot totals accept legacy (reachable/dormant) or current
 * (connectedNow/disconnected) connectivity pairs.  At least one complete
 * pair must be present and partial pairs are rejected.
 */
const snapshotTotalsBaseSchema = z.object({
  allActive: z.number().int().nonnegative(),
  uninstalled: z.number().int().nonnegative(),
  connectedNow: z.number().int().nonnegative().optional(),
  disconnected: z.number().int().nonnegative().optional(),
  reachable: z.number().int().nonnegative().optional(),
  dormant: z.number().int().nonnegative().optional(),
});

function validateConnectivityPairs(
  d: z.infer<typeof snapshotTotalsBaseSchema>,
  ctx: z.RefinementCtx,
) {
  const hasConnected = d.connectedNow != null && d.disconnected != null;
  const hasReachable = d.reachable != null && d.dormant != null;
  const partialConnected = (d.connectedNow != null) !== (d.disconnected != null);
  const partialReachable = (d.reachable != null) !== (d.dormant != null);

  if (partialConnected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'connectedNow and disconnected must both be present or both absent',
    });
  }
  if (partialReachable) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'reachable and dormant must both be present or both absent',
    });
  }
  if (!hasConnected && !hasReachable) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'At least one complete connectivity pair is required (connectedNow/disconnected or reachable/dormant)',
    });
  }
}

export const hostNonsoleParticipationSnapshotTotalsSchema =
  snapshotTotalsBaseSchema.superRefine(validateConnectivityPairs);
export type HostNonsoleParticipationSnapshotTotals = z.infer<
  typeof hostNonsoleParticipationSnapshotTotalsSchema
>;

export const hostNonsoleParticipationSnapshotSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  totals: hostNonsoleParticipationSnapshotTotalsSchema,
  inHostMode: hostNonsoleParticipationInHostModeSchema,
  reasons: z.array(participationReasonRowSchema),
  gates: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export type HostNonsoleParticipationSnapshot = z.infer<
  typeof hostNonsoleParticipationSnapshotSchema
>;

export const hostNonsoleParticipationTrendResponseSchema = z.object({
  snapshots: z.array(hostNonsoleParticipationSnapshotSchema),
});
export type HostNonsoleParticipationTrendResponse = z.infer<
  typeof hostNonsoleParticipationTrendResponseSchema
>;

// ============================================================================
// Daily accumulator document (MongoDB persistence shape)
// ============================================================================

export const hostNonsoleParticipationDailyDocumentSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reachabilityDays: z.number().int().positive(),
  staleMinutes: z.number().int().positive(),
  sampleCount: z.number().int().positive(),
  sums: snapshotTotalsBaseSchema
    .extend({
      hosting: z.number().int().nonnegative(),
      idle: z.number().int().nonnegative(),
      reasons: z.record(z.string(), z.number().int().nonnegative()),
      gates: z.record(z.string(), z.number().int().nonnegative()).optional(),
    })
    .superRefine(validateConnectivityPairs),
});
export type HostNonsoleParticipationDailyDocument = z.infer<
  typeof hostNonsoleParticipationDailyDocumentSchema
>;

// ============================================================================
// Drill-down machine row
// ============================================================================

export const hostNonsoleParticipationMachineRowSchema = z.object({
  machineId: z.string(),
  hostname: z.string().nullable().optional(),
  lastKnownState: z.string().nullable().optional(),
  nodeServiceLastSeenAt: z.string().nullable().optional(),
  evaluatedAtUtc: z.string().nullable().optional(),
  bucket: participationBucketSchema,
  blockingGateIds: z.array(z.string()),
  blockingReasons: z.array(
    z.object({
      gateId: z.string(),
      reasonCode: z.string(),
    }),
  ),
  presenceProcesses: z.array(z.string()).optional(),
});
export type HostNonsoleParticipationMachineRow = z.infer<
  typeof hostNonsoleParticipationMachineRowSchema
>;

// ============================================================================
// Cohort query / result
// ============================================================================

export const cohortClauseSchema = z.object({
  gateId: z.string(),
  reasonCode: z.string().optional(),
});
export type CohortClause = z.infer<typeof cohortClauseSchema>;

export const cohortGroupSchema = z.object({
  op: z.enum(['OR']),
  clauses: z.array(cohortClauseSchema).min(1).max(8),
});
export type CohortGroup = z.infer<typeof cohortGroupSchema>;

export const hostNonsoleParticipationCohortQuerySchema = z.object({
  groups: z.array(cohortGroupSchema).min(1).max(4),
  groupOp: z.enum(['AND']),
  includeMachines: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});
export type HostNonsoleParticipationCohortQuery = z.infer<
  typeof hostNonsoleParticipationCohortQuerySchema
>;

export const hostNonsoleParticipationCohortResultSchema = z.object({
  machineCount: z.number().int().nonnegative(),
  machines: z.array(hostNonsoleParticipationMachineRowSchema).optional(),
  nextCursor: z.string().optional(),
  generatedAt: z.number(),
});
export type HostNonsoleParticipationCohortResult = z.infer<
  typeof hostNonsoleParticipationCohortResultSchema
>;

// ============================================================================
// Reason breakdown (per-gate + per-reasonCode)
// ============================================================================

export const gateReasonBreakdownRowSchema = z.object({
  gateId: z.string(),
  count: z.number().int().nonnegative(),
  reasonCodes: z.array(
    z.object({
      reasonCode: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type GateReasonBreakdownRow = z.infer<typeof gateReasonBreakdownRowSchema>;

export const hostNonsoleParticipationReasonBreakdownSchema = z.object({
  generatedAt: z.number(),
  gates: z.array(gateReasonBreakdownRowSchema),
});
export type HostNonsoleParticipationReasonBreakdown = z.infer<
  typeof hostNonsoleParticipationReasonBreakdownSchema
>;

// ============================================================================
// Top offending processes (presence-hold aggregation)
// ============================================================================

export const hostNonsoleParticipationTopProcessesSchema = z.object({
  generatedAt: z.number(),
  processes: z
    .array(
      z.object({
        processName: z.string(),
        count: z.number().int().nonnegative(),
      }),
    )
    .max(50),
});
export type HostNonsoleParticipationTopProcesses = z.infer<
  typeof hostNonsoleParticipationTopProcessesSchema
>;

// ============================================================================
// Blocker Combinations (Phase 4)
// ============================================================================

export const blockerCombinationRowSchema = z.object({
  gateIds: z.array(z.string()).min(1).max(20),
  count: z.number().int().nonnegative(),
});
export type BlockerCombinationRow = z.infer<typeof blockerCombinationRowSchema>;

export const hostNonsoleParticipationCombinationsSchema = z.object({
  generatedAt: z.number(),
  combinations: z.array(blockerCombinationRowSchema).max(50),
});
export type HostNonsoleParticipationCombinations = z.infer<
  typeof hostNonsoleParticipationCombinationsSchema
>;

// ============================================================================
// Fleet filter vocabulary (v3)
// Canonical definition consumed by both the server Redis cache parser
// (fleetFilterVocabulary.ts) and the client fetch wrapper (fleetApi.ts).
// ============================================================================

export const fleetFilterVocabularySchema = z.object({
  blockingGateIds: z.array(z.string()).default([]),
  blockingReasonCodes: z.array(z.string()).default([]),
  presenceProcesses: z.array(z.string()).default([]),
  policyProcesses: z.array(z.string()).default([]),
  steamGameAppIds: z.array(z.string()).default([]),
  emulatorPackages: z.array(z.string()).default([]),
  primaryResolutions: z.array(z.string()).default([]),
  physicalDisplayCounts: z.array(z.string()).default([]),
  // Phase 1 (admin-dash fleet-filters-v2): device-type / disqualification-
  // reason / geo vocabulary. All default `[]` so old cached vocabulary
  // documents parse fine (parser uses `safeParse` with defaults).
  deviceTypes: z.array(z.string()).default([]),
  disqualificationReasons: z.array(z.string()).default([]),
  geohashes: z.array(z.string()).default([]),
  cities: z.array(z.string()).default([]),
  states: z.array(z.string()).default([]),
  countries: z.array(z.string()).default([]),
});
export type FleetFilterVocabulary = z.infer<typeof fleetFilterVocabularySchema>;

/**
 * Response contract for the fleet-wide gaming-disqualification counts endpoint
 * (`GET /realtime/admin/fleet/gaming-disqualifications`).
 *
 * Server-computed over the WHOLE filtered fleet (never a page-scoped client
 * aggregate), and parsed at both ends so a shape change fails loudly instead of
 * silently rendering `undefined` counts.
 */
export const fleetGamingDisqualificationCountsSchema = z.object({
  counts: z.array(
    z.object({
      componentKey: z.string().min(1),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type FleetGamingDisqualificationCounts = z.infer<
  typeof fleetGamingDisqualificationCountsSchema
>;

// ============================================================================
// Fleet extra filter params (v3)
// ============================================================================

export const fleetHeartbeatFilterSchema = z.enum(['connected', 'disconnected']);
export type FleetHeartbeatFilter = z.infer<typeof fleetHeartbeatFilterSchema>;

export const fleetLifecycleFilterSchema = z.enum(['active', 'uninstalled', 'all']);
export type FleetLifecycleFilter = z.infer<typeof fleetLifecycleFilterSchema>;

export const MAX_FLEET_PROCESS_FILTER_VALUES = 20;

export const fleetExtraFilterParamsSchema = z.object({
  heartbeat: fleetHeartbeatFilterSchema.optional(),
  hostMode: z.boolean().optional(),
  presenceProcess: z
    .array(z.string().min(1).max(260))
    .max(MAX_FLEET_PROCESS_FILTER_VALUES)
    .optional(),
  policyProcess: z
    .array(z.string().min(1).max(260))
    .max(MAX_FLEET_PROCESS_FILTER_VALUES)
    .optional(),
  lifecycle: fleetLifecycleFilterSchema.optional(),
});
export type FleetExtraFilterParams = z.infer<typeof fleetExtraFilterParamsSchema>;

/**
 * Wire-format parser for query-string values (Express `req.query`).
 * Coerces string query-param values into typed fields:
 * - `heartbeat` / `lifecycle`: accept the enum string as-is.
 * - `hostMode`: accepts `'true'`/`'false'` (strings) or boolean.
 * - `presenceProcess` / `policyProcess`: accepts a comma-separated string
 *   (parsed into an array) or an existing string[] passthrough.
 * Throws with a Zod-derived message on invalid input.
 */
export function parseFleetExtraFilterParams(
  query: Record<string, unknown>,
): FleetExtraFilterParams {
  const raw: Record<string, unknown> = {};
  if (query['heartbeat'] !== undefined) raw['heartbeat'] = query['heartbeat'];
  if (query['lifecycle'] !== undefined) raw['lifecycle'] = query['lifecycle'];
  if (query['presenceProcess'] !== undefined) {
    const parsed = parseCommaListField(query['presenceProcess']);
    if (parsed) raw['presenceProcess'] = parsed;
  }
  if (query['policyProcess'] !== undefined) {
    const parsed = parseCommaListField(query['policyProcess']);
    if (parsed) raw['policyProcess'] = parsed;
  }
  if (query['hostMode'] !== undefined) {
    const v = query['hostMode'];
    if (v === 'true' || v === true) raw['hostMode'] = true;
    else if (v === 'false' || v === false) raw['hostMode'] = false;
    else raw['hostMode'] = v;
  }
  const result = fleetExtraFilterParamsSchema.parse(raw);
  return result;
}

function parseCommaListField(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const parts = value
      .filter((v): v is string => typeof v === 'string')
      .map((v) => v.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : undefined;
}

// ============================================================================
// Gaming-score filters + virtualization (Phase 1: admin-dash fleet-failures)
// ============================================================================

/**
 * Gaming-score range endpoints for the Fleet filters (frozen contract,
 * Phase 1 of the admin-dash fleet-failures plan):
 *
 * - `gamingScoreMin` — number, inclusive lower bound of 0.0–100.0. Omit = unbounded.
 * - `gamingScoreMax` — number, inclusive upper bound of 0.0–100.0. Omit = unbounded.
 * - `virtualizationEnabled` — `'true'` | `'false'` only; absence = no filter.
 * - When either score bound is set, machines without a `gamingScore` are EXCLUDED.
 *
 * `fleetRoutes.ts` (Phase 2) parses these query params; Phase 1 declares the
 * validation here so the contract is shared between server (Phase 2) and
 * client (Phase 3).
 */
export const GAMING_SCORE_FILTER_MIN = 0;
export const GAMING_SCORE_FILTER_MAX = 100;

export const gamingScoreMinFilterSchema = z
  .number()
  .min(GAMING_SCORE_FILTER_MIN)
  .max(GAMING_SCORE_FILTER_MAX);
export const gamingScoreMaxFilterSchema = z
  .number()
  .min(GAMING_SCORE_FILTER_MIN)
  .max(GAMING_SCORE_FILTER_MAX);
export const virtualizationEnabledFilterSchema = z.boolean();

// strict() so a gamingScores vocabulary field — or any other unknown key — is
// rejected: the Phase 1 contract allows only the three filter params.
export const fleetGamingScoreFilterParamsSchema = z
  .object({
    gamingScoreMin: gamingScoreMinFilterSchema.optional(),
    gamingScoreMax: gamingScoreMaxFilterSchema.optional(),
    virtualizationEnabled: virtualizationEnabledFilterSchema.optional(),
  })
  .strict()
  .superRefine((params, ctx) => {
    if (
      params.gamingScoreMin != null &&
      params.gamingScoreMax != null &&
      params.gamingScoreMin > params.gamingScoreMax
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'gamingScoreMin must be <= gamingScoreMax',
      });
    }
  });
export type FleetGamingScoreFilterParams = z.infer<typeof fleetGamingScoreFilterParamsSchema>;

/** Parses a numeric query-param value as a finite JSON number (no coercion). */
function parseScoreBoundParam(value: unknown): number {
  if (typeof value !== 'number' && typeof value !== 'string') {
    throw new Error(`gamingScore must be a number, got ${typeof value}`);
  }
  if (typeof value === 'string' && value.trim() === '') {
    throw new Error('gamingScore must be a number, got empty string');
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`gamingScore must be a finite number, got ${JSON.stringify(value)}`);
  }
  return parsed;
}

/**
 * Wire-format parser for the gaming-score filter query params (Express
 * `req.query` values are strings). Numeric bounds are parsed strictly
 * (numbers only, inclusive [0, 100]); `virtualizationEnabled` accepts
 * `'true'`/`'false'` or booleans. Throws with a Zod-derived message on invalid
 * input — invalid bounds are never silently dropped.
 */
export function parseFleetGamingScoreFilterParams(
  query: Record<string, unknown>,
): FleetGamingScoreFilterParams {
  const raw: Record<string, unknown> = {};
  if (query['gamingScoreMin'] !== undefined) {
    raw['gamingScoreMin'] = parseScoreBoundParam(query['gamingScoreMin']);
  }
  if (query['gamingScoreMax'] !== undefined) {
    raw['gamingScoreMax'] = parseScoreBoundParam(query['gamingScoreMax']);
  }
  if (query['virtualizationEnabled'] !== undefined) {
    const v = query['virtualizationEnabled'];
    if (v === 'true' || v === true) raw['virtualizationEnabled'] = true;
    else if (v === 'false' || v === false) raw['virtualizationEnabled'] = false;
    else raw['virtualizationEnabled'] = v;
  }
  return fleetGamingScoreFilterParamsSchema.parse(raw);
}

// ============================================================================
// Gaming disqualification derivation (Phase 1: admin-dash fleet-failures)
// ============================================================================

/**
 * Minimal structural shape of the persisted gaming-score component scores.
 * Deliberately NOT imported from `@playcastdotio/Messaging` (this lib avoids
 * that dependency edge): only the fields the derivation rules read are
 * declared, mirroring the defensive access `GamingScoreDetailsModal.tsx` uses.
 */
export interface GamingScoreComponentScoreShape {
  score?: number;
  status?: string;
  meetsMinimum?: boolean;
  maxScore?: number;
  details?: unknown;
}

/** Ordered keys inspected by `deriveGamingDisqualifiers`. */
export const GAMING_SCORE_COMPONENT_KEYS = [
  'environment',
  'videoEncoding',
  'trust',
  'osVersion',
  'cpu',
  'gpu',
  'memory',
  'storage',
  'network',
] as const;

/** Stable per-component default reasons when `status` carries no post-prefix text. */
export const GAMING_DISQUALIFIER_DEFAULT_REASONS: Record<string, string> = {
  environment: 'Environment does not meet minimum requirements',
  videoEncoding: 'Video encoding does not meet minimum requirements',
  trust: 'Trust check failed',
  osVersion: 'Operating system version not supported',
  cpu: 'CPU does not meet minimum requirements',
  gpu: 'GPU does not meet minimum requirements',
  memory: 'Memory does not meet minimum requirements',
  storage: 'Storage does not meet minimum requirements',
  network: 'Network does not meet minimum requirements',
};

/**
 * One derived reason a machine's gaming score is 0 or it is disqualified.
 * `componentKey`/`label` are machine-parseable; `reason` is human-readable.
 */
export interface GamingDisqualifier {
  /** Component key, e.g. 'environment'. */
  componentKey: string;
  /** Short machine-parseable label, e.g. 'environment', 'videoEncoding'. */
  label: string;
  /** Human-readable reason sentence, e.g. 'Virtual machine detected'. */
  reason: string;
}

/**
 * Minimal structural shape of the gaming-score full-data payload consumed by
 * the fallback path of `deriveGamingDisqualifiers`.
 */
export interface GamingScoreFullDataShape {
  overallStatus?: string;
  recommendations?: string[];
}

/**
 * Pure derivation of the reasons a machine's game score is 0 / it is
 * disqualified (frozen rules, Phase 1 of the admin-dash fleet-failures plan).
 *
 * Examines each component in `components` in `GAMING_SCORE_COMPONENT_KEYS`
 * order; a component produces one entry when:
 * - `component.meetsMinimum === false`, OR
 * - `component.status` starts with `DISQUALIFIED`.
 *
 * (`maxScore === 0` AND `score === 0` with `meetsMinimum === false` is a
 * strict subset of the first clause, so it needs no separate test — the first
 * clause alone preserves the frozen semantics.)
 *
 * `reason` is `status` stripped of its `DISQUALIFIED`-style prefix when the
 * text survives, else the component's stable default. When `components` is
 * absent/empty, falls back to `fullData` (at most one entry,
 * `componentKey: 'overall'`).
 */
export function deriveGamingDisqualifiers(
  fullData: GamingScoreFullDataShape | undefined | null,
  components: Record<string, GamingScoreComponentScoreShape> | undefined | null,
): GamingDisqualifier[] {
  // `meetsMinimum` is read once up-front: re-reading the property inside the
  // OR chain lets the compiler narrow it after the first `=== false` check,
  // which TypeScript rejects with TS2367 (no overlap) on later clauses.
  const gathered = components
    ? GAMING_SCORE_COMPONENT_KEYS.flatMap((key) => {
        const component = components[key];
        if (!component) return [];
        const meetsMinimum = component.meetsMinimum;
        const aboutFace =
          meetsMinimum === false || (component.status ?? '').toUpperCase().startsWith('DISQUALIFIED');
        if (!aboutFace) return [];
        return [
          {
            componentKey: key,
            label: key,
            reason: disqualifierReason(key, component.status),
          },
        ];
      })
    : [];

  if (gathered.length > 0) return gathered;

  if (components && Object.keys(components).length > 0) return [];

  const overallStatus = fullData?.overallStatus;
  if (!overallStatus) return [];
  if (overallStatus.toUpperCase().startsWith('DISQUALIFIED')) {
    return [
      {
        componentKey: 'overall',
        label: 'overall',
        reason: stripDisqualifierPrefix(overallStatus) || 'Machine is disqualified',
      },
    ];
  }
  const recommendations = fullData?.recommendations ?? [];
  const first = recommendations.find(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
  );
  if (first) {
    return [{ componentKey: 'overall', label: 'overall', reason: first }];
  }
  return [];
}

/**
 * Reason text for a component entry: `status` with its `DISQUALIFIED`-style
 * prefix removed when text survives, else the component's stable default.
 */
function disqualifierReason(componentKey: string, status: string | undefined): string {
  const stripped = stripDisqualifierPrefix(status);
  if (stripped) return stripped;
  return GAMING_DISQUALIFIER_DEFAULT_REASONS[componentKey] ?? `${componentKey} check failed`;
}

/**
 * Strips a leading `DISQUALIFIED - ` / `DISQUALIFIED: ` prefix from a status
 * string. Other prefixes (`ERROR - ...`, `EXCELLENT - ...`) are NOT stripped:
 * per the frozen Phase 1 derivation rules the component reason is the
 * post-DISQUALIFIED-prefix text, and only that. Returns `undefined` when there
 * is no text after the prefix.
 */
function stripDisqualifierPrefix(status: string | undefined): string | undefined {
  if (status !== undefined && status.trim().length > 0) {
    const match = status.match(/^DISQUALIFIED[\s]*[-:]\s*(.+)$/i);
    if (match && match[1].trim().length > 0) return match[1].trim();
  }
  return undefined;
}

// ============================================================================
// Gaming virtualization derivation (Phase 1: admin-dash fleet-failures)
// ============================================================================

/**
 * Minimal structural shape for the parts of an environment details object
 * `deriveVirtualizationEnabled` reads.
 */
export interface GamingScoreEnvironmentDetailsShape {
  isVirtualMachine?: boolean;
  scoringFactors?: {
    isVirtualMachine?: boolean;
    virtualizationFirmwareEnabled?: boolean;
    virtualizationFirmwareQueried?: boolean;
  };
}

/**
 * Tri-state virtualization derivation (frozen rules, Phase 1 of the
 * admin-dash fleet-failures plan):
 *
 * - `true` when `scoringFactors.virtualizationFirmwareEnabled === true` OR the
 *   machine is itself a VM (`scoringFactors.isVirtualMachine === true` or
 *   `details.isVirtualMachine === true`).
 * - `false` only when the firmware signal is `false` AND
 *   `scoringFactors.virtualizationFirmwareQueried === true` (the probe ran)
 *   AND the machine is not a VM.
 * - `null` when no signal is present (not reported).
 */
export function deriveVirtualizationEnabled(
  components: Record<string, GamingScoreComponentScoreShape> | undefined | null,
): boolean | null {
  const environment = components?.['environment'];
  const details = (environment?.details ?? undefined) as
    | GamingScoreEnvironmentDetailsShape
    | undefined;
  const scoringFactors = details?.scoringFactors;

  if (scoringFactors?.virtualizationFirmwareEnabled === true) {
    return true;
  }
  if (scoringFactors?.isVirtualMachine === true || details?.isVirtualMachine === true) {
    return true;
  }
  // `false` is only meaningful when the probe actually ran. The producer
  // persists `virtualizationFirmwareEnabled = Boolean(... ?? false)` even for
  // hosts where the VM flag was never queried (`virtualizationFirmwareQueried`
  // false/absent), so gate the negative branch on an explicit query — otherwise
  // every unprobed host would surface under the "VT Off" filter.
  if (
    scoringFactors?.virtualizationFirmwareEnabled === false &&
    scoringFactors.virtualizationFirmwareQueried === true
  ) {
    return false;
  }
  return null;
}

// ============================================================================
// Device type derivation (Phase 1: admin-dash fleet-filters-v2)
// ============================================================================

export type DeviceTypeValue = 'desktop' | 'laptop' | 'unknown';

/**
 * Pure derivation of a host's device type from its gaming-score environment
 * details (frozen rules, Phase 1 of the admin-dash fleet-filters-v2 plan):
 * read `gamingScoreComponents.environment.details.deviceType`; `'desktop'`
 * → `'desktop'`, `'laptop'` → `'laptop'`; when the field is absent but
 * `details.isLaptop === true` → `'laptop'`; otherwise `'unknown'`.
 *
 * Input is the raw component-scores payload (or `null`/`undefined`), so callers
 * can pass persisted documents and API responses alike without pre-shaping.
 */
export function deriveDeviceType(
  gamingScoreComponents: Record<string, unknown> | null | undefined,
): DeviceTypeValue {
  const environment = (gamingScoreComponents ?? undefined)?.['environment'];
  if (environment && typeof environment === 'object') {
    const details = (environment as { details?: unknown }).details;
    if (details && typeof details === 'object') {
      const deviceType = (details as { deviceType?: unknown }).deviceType;
      // The persisted value is PascalCase ('Desktop'/'Laptop' — see
      // laptopDetection.ts), while the filter vocabulary is lowercase. Match
      // case-insensitively and normalize to the lowercase contract.
      if (typeof deviceType === 'string') {
        const normalized = deviceType.toLowerCase();
        if (normalized === 'desktop' || normalized === 'laptop') return normalized as DeviceTypeValue;
      }
      if (deviceType === undefined) {
        // Legacy fallback: only when no explicit deviceType is present.
        if ((details as { isLaptop?: unknown }).isLaptop === true) return 'laptop';
      }
      // Explicit but invalid deviceType (e.g. 'tablet') → unknown, not laptop.
    }
  }
  return 'unknown';
}

// ============================================================================
// Geo filter params (Phase 1: admin-dash fleet-filters-v2)
// ============================================================================

export const MAX_FLEET_GEO_FILTER_VALUES = 50;

/**
 * Wire-format params for the four geo filter families (frozen contract,
 * Phase 1 of the admin-dash fleet-filters-v2 plan), shared between the Fleet
 * REST route (Phase 2) and the client (Phase 3):
 *
 * - `geohash` — comma-separated multi-select, `$in` semantic.
 * - `city` — comma-separated multi-select, `$in` semantic.
 * - `state` — ISO 3166-2 subdivision codes (e.g. `US-CA`), `$in` semantic.
 * - `country` — ISO 3166-1 alpha-2 codes (e.g. `US`), `$in` semantic.
 *
 * All fields optional + additive; `.strict()` rejects any unknown key.
 */
export const fleetGeoFilterParamsSchema = z
  .object({
    geohash: z.array(z.string()).max(MAX_FLEET_GEO_FILTER_VALUES).optional(),
    city: z.array(z.string()).max(MAX_FLEET_GEO_FILTER_VALUES).optional(),
    state: z.array(z.string()).max(MAX_FLEET_GEO_FILTER_VALUES).optional(),
    country: z.array(z.string()).max(MAX_FLEET_GEO_FILTER_VALUES).optional(),
  })
  .strict();
export type FleetGeoFilterParams = z.infer<typeof fleetGeoFilterParamsSchema>;

// ============================================================================
// Device type + disqualification reason filter params (Phase 1: admin-dash
// fleet-filters-v2) — parse these on top of the geo family.
// ============================================================================

/**
 * Allowed literal `deviceType` filter values. `'unknown'` is deliberately NOT
 * allowed: it is a CLIENT-side derivation (machines with no device-type signal),
 * never a value persisted across the fleet — the server matches stored literals
 * only (`$in` on `gamingScoreComponents.environment.details.deviceType`).
 */
export const FLEET_DEVICE_TYPE_FILTER_VALUES = ['desktop', 'laptop'] as const;

export const fleetDeviceTypeFilterSchema = z.array(z.enum(FLEET_DEVICE_TYPE_FILTER_VALUES));

/** Wire-format params for the `deviceType` filter family. */
export const fleetDeviceTypeFilterParamsSchema = z
  .object({
    deviceType: fleetDeviceTypeFilterSchema.max(MAX_FLEET_GEO_FILTER_VALUES).optional(),
  })
  .strict();
export type FleetDeviceTypeFilterParams = z.infer<typeof fleetDeviceTypeFilterParamsSchema>;

/**
 * Disqualification-reason filter: `GAMING_SCORE_COMPONENT_KEYS` plus the
 * `'overall'` fallback key (the full-data fallback of the server/client
 * derivation). Values outside the set are rejected — unknown reasons can never
 * match anything, so accepting them would silently return an empty fleet.
 */
export const FLEET_DISQUALIFICATION_REASON_VALUES = [
  ...GAMING_SCORE_COMPONENT_KEYS,
  'overall',
] as const;

export const fleetDisqualificationReasonFilterSchema = z.array(
  z.enum(FLEET_DISQUALIFICATION_REASON_VALUES),
);

/** Wire-format params for the `disqualificationReason` filter family. */
export const fleetDisqualificationReasonFilterParamsSchema = z
  .object({
    disqualificationReason: fleetDisqualificationReasonFilterSchema
      .max(MAX_FLEET_GEO_FILTER_VALUES)
      .optional(),
  })
  .strict();
export type FleetDisqualificationReasonFilterParams = z.infer<
  typeof fleetDisqualificationReasonFilterParamsSchema
>;

const parseCommaListForFilter = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    const parts = value
      .filter((v): v is string => typeof v === 'string')
      .map((v) => v.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : undefined;
};

/**
 * Wire-format parser for the four geo filter families (Phase 1, admin-dash
 * fleet-filters-v2). Accepts comma-separated query-param strings (parsed into
 * arrays) or existing string[] passthroughs; each array is capped at
 * `MAX_FLEET_GEO_FILTER_VALUES` (50). `.strict()` rejects unknown keys; a
 * Zod-derived message is thrown on invalid input.
 */
export function parseFleetGeoFilterParams(
  query: Record<string, unknown>,
): FleetGeoFilterParams {
  const raw: Record<string, unknown> = {};
  for (const key of ['geohash', 'city', 'state', 'country'] as const) {
    const parsed = parseCommaListForFilter(query[key]);
    if (parsed) raw[key] = parsed;
  }
  return fleetGeoFilterParamsSchema.parse(raw);
}

/**
 * Wire-format parser for the `deviceType` filter (Phase 1, admin-dash
 * fleet-filters-v2). Accepts a comma-separated string or string[] passthrough;
 * each value must be `desktop` | `laptop` (`'unknown'` is rejected — it is a
 * client-side derivation, never a wire value), and the list is capped at 50.
 */
export function parseFleetDeviceTypeFilterParams(
  query: Record<string, unknown>,
): FleetDeviceTypeFilterParams {
  const parsed = parseCommaListForFilter(query['deviceType']);
  const raw: Record<string, unknown> = {};
  if (parsed) raw['deviceType'] = parsed;
  return fleetDeviceTypeFilterParamsSchema.parse(raw);
}

/**
 * Wire-format parser for the `disqualificationReason` filter (Phase 1,
 * admin-dash fleet-filters-v2). Accepts a comma-separated string or string[]
 * passthrough; each value must be a `GAMING_SCORE_COMPONENT_KEYS` member or
 * `'overall'`, capped at 50.
 */
export function parseFleetDisqualificationReasonFilterParams(
  query: Record<string, unknown>,
): FleetDisqualificationReasonFilterParams {
  const parsed = parseCommaListForFilter(query['disqualificationReason']);
  const raw: Record<string, unknown> = {};
  if (parsed) raw['disqualificationReason'] = parsed;
  return fleetDisqualificationReasonFilterParamsSchema.parse(raw);
}

// ============================================================================
// Campaign-failure distinct reasons (admin-dash fleet-filters-v2)
// ============================================================================

/**
 * Cross-process response contract for
 * `GET /realtime/admin/campaign-failures/reasons`, shared between the
 * realtime-api route (which parses before responding) and the admin-dashboard
 * client fetch wrapper (which parses before returning) so neither end can
 * drift from the other.
 *
 * `reasons` is the distinct, sorted, NON-empty failure-reason set: the
 * distinct reasons present across matching campaign failures, sorted
 * lexicographically; a failure with no reason is excluded from the set.
 */
export const campaignFailuresReasonsResponseSchema = z.object({
  reasons: z.array(z.string().min(1)),
});
export type CampaignFailuresReasonsResponse = z.infer<
  typeof campaignFailuresReasonsResponseSchema
>;

// ============================================================================
// Helpers
// ============================================================================

export function buildEmptyReasonCounts(): Record<ParticipationBucket, number> {
  return Object.fromEntries(participationBucketValues.map((b) => [b, 0])) as Record<
    ParticipationBucket,
    number
  >;
}

export function buildReasonRows(
  counts: Record<ParticipationBucket, number>,
  includeBuckets: ParticipationBucket[] = NON_PARTICIPATION_BUCKETS,
): ParticipationReasonRow[] {
  return includeBuckets
    .filter((bucket) => counts[bucket] > 0)
    .map((bucket) => ({
      bucket,
      label: PARTICIPATION_BUCKET_LABELS[bucket],
      count: counts[bucket],
    }));
}
